import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { calculateMortgage } from 'utils/mortgage-calculator/mortgageCalculations';
import { buildClassName } from 'utils/build-class-name';
import styles from './ScenarioComparison.module.scss';

interface Scenario {
  id: string;
  name: string;
  homePrice: number;
  downPayment: number;
  interestRate: number;
  amortization: number;
}

const ScenarioComparison = () => {
  const [scenarios, setScenarios] = useState<Scenario[]>([
    {
      id: '1',
      name: 'Current Plan',
      homePrice: 800000,
      downPayment: 160000,
      interestRate: 5.25,
      amortization: 25,
    },
  ]);

  const addScenario = () => {
    const newScenario: Scenario = {
      id: Date.now().toString(),
      name: `Scenario ${scenarios.length + 1}`,
      homePrice: 800000,
      downPayment: 160000,
      interestRate: 5.25,
      amortization: 25,
    };
    setScenarios([...scenarios, newScenario]);
  };

  const removeScenario = (id: string) => {
    if (scenarios.length > 1) {
      setScenarios(scenarios.filter(s => s.id !== id));
    }
  };

  const updateScenario = (id: string, field: keyof Scenario, value: string | number) => {
    setScenarios(
      scenarios.map(s => (s.id === id ? { ...s, [field]: value } : s))
    );
  };

  return (
    <div className={styles.component}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h3 className={styles.headerTitle}>
            Compare Scenarios
          </h3>
          <p className={styles.headerSubtitle}>
            Compare different mortgage options side by side
          </p>
        </div>
        <Button
          onClick={addScenario}
          disabled={scenarios.length >= 3}
          variant="outline"
          size="sm"
          className={styles.addButton}
        >
          <span>➕</span> Add Scenario
        </Button>
      </div>

      <div className={styles.scenariosGrid}>
        {scenarios.map(scenario => {
          const results = calculateMortgage(
            scenario.homePrice,
            scenario.downPayment,
            scenario.interestRate,
            scenario.amortization,
            0,
            0,
            0,
            false
          );

          return (
            <Card key={scenario.id} className={styles.scenarioCard}>
              {scenarios.length > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className={styles.removeButton}
                  onClick={() => removeScenario(scenario.id)}
                >
                  <span>✕</span>
                </Button>
              )}
              <CardHeader className={styles.scenarioHeader}>
                <Input
                  value={scenario.name}
                  onChange={e => updateScenario(scenario.id, 'name', e.target.value)}
                  style={{ fontWeight: 600, textAlign: 'center', border: 'none', padding: 0 }}
                />
              </CardHeader>
              <CardContent className={styles.inputGrid}>
                <div className={styles.inputGroup}>
                  <Label style={{ fontSize: '0.75rem' }}>Home Price</Label>
                  <Input
                    type="number"
                    value={scenario.homePrice}
                    onChange={e =>
                      updateScenario(scenario.id, 'homePrice', Number(e.target.value))
                    }
                    style={{ marginTop: '0.25rem' }}
                  />
                </div>
                <div className={styles.inputGroup}>
                  <Label style={{ fontSize: '0.75rem' }}>Down Payment ($)</Label>
                  <Input
                    type="number"
                    value={scenario.downPayment}
                    onChange={e =>
                      updateScenario(scenario.id, 'downPayment', Number(e.target.value))
                    }
                    style={{ marginTop: '0.25rem' }}
                  />
                  <div style={{ fontSize: '0.75rem', color: '$color-dark-navy-80', marginTop: '0.25rem' }}>
                    {((scenario.downPayment / scenario.homePrice) * 100).toFixed(1)}% down payment
                    {scenario.downPayment / scenario.homePrice < 0.2 &&
                      <span style={{ color: '#d97706' }}> • CMHC required</span>
                    }
                  </div>
                </div>
                <div className={styles.inputGroup}>
                  <Label style={{ fontSize: '0.75rem' }}>Interest Rate (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={scenario.interestRate}
                    onChange={e =>
                      updateScenario(scenario.id, 'interestRate', Number(e.target.value))
                    }
                    style={{ marginTop: '0.25rem' }}
                  />
                </div>
                <div className={styles.inputGroup}>
                  <Label style={{ fontSize: '0.75rem' }}>Amortization (years)</Label>
                  <Input
                    type="number"
                    value={scenario.amortization}
                    onChange={e =>
                      updateScenario(scenario.id, 'amortization', Number(e.target.value))
                    }
                    style={{ marginTop: '0.25rem' }}
                  />
                </div>

                <div className={styles.resultsGrid}>
                  <div className={styles.resultRow}>
                    <span className={styles.resultLabel}>Monthly Payment</span>
                    <span className={styles.resultValue} style={{ color: 'var(--themes-colors-primary-background)' }}>
                      ${results.monthlyPayment.toLocaleString()}
                    </span>
                  </div>
                  <div className={styles.resultRow}>
                    <span className={styles.resultLabel}>Principal</span>
                    <span className={styles.resultValue}>
                      ${results.principalAmount.toLocaleString()}
                    </span>
                  </div>
                  <div className={styles.resultRow}>
                    <span className={styles.resultLabel}>Total Interest</span>
                    <span className={styles.resultValue}>
                      ${results.totalInterest.toLocaleString()}
                    </span>
                  </div>
                  {results.cmhcInsurance > 0 && (
                    <div className={styles.resultRow}>
                      <span className={styles.resultLabel}>CMHC Insurance</span>
                      <span className={styles.resultValue} style={{ color: '#d97706' }}>
                        ${results.cmhcInsurance.toLocaleString()}
                      </span>
                    </div>
                  )}
                  <div className={styles.resultRowTotal}>
                    <span className={buildClassName(styles.resultLabel, styles.resultValue)} style={{ fontWeight: 700 }}>Total Cost</span>
                    <span className={styles.resultValue} style={{ fontWeight: 700 }}>
                      ${results.totalPayment.toLocaleString()}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {scenarios.length >= 2 && (
        <Card className={styles.comparisonCard}>
          <CardContent style={{ paddingTop: '1.5rem' }}>
            <h4 style={{ fontWeight: 600, marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>➡️</span> Key Differences (Scenario 1 vs Scenario 2)
            </h4>
            <div style={{ fontSize: '0.875rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {(() => {
                const result1 = calculateMortgage(scenarios[0].homePrice, scenarios[0].downPayment, scenarios[0].interestRate, scenarios[0].amortization, 0, 0, 0, false);
                const result2 = calculateMortgage(scenarios[1].homePrice, scenarios[1].downPayment, scenarios[1].interestRate, scenarios[1].amortization, 0, 0, 0, false);
                const paymentDiff = result1.monthlyPayment - result2.monthlyPayment;
                const interestDiff = result1.totalInterest - result2.totalInterest;
                const cmhcDiff = result1.cmhcInsurance - result2.cmhcInsurance;
                const totalDiff = result1.totalPayment - result2.totalPayment;

                return (
                  <>
                    <p style={{ color: paymentDiff > 0 ? '#dc2626' : '#16a34a' }}>
                      • Monthly payment: {paymentDiff > 0 ? '+' : ''}${paymentDiff.toLocaleString()}
                      {paymentDiff > 0 ? ' more expensive' : ' less expensive'}
                    </p>
                    <p style={{ color: interestDiff > 0 ? '#dc2626' : '#16a34a' }}>
                      • Total interest: {interestDiff > 0 ? '+' : ''}${interestDiff.toLocaleString()}
                      {interestDiff > 0 ? ' more' : ' less'}
                    </p>
                    {(result1.cmhcInsurance > 0 || result2.cmhcInsurance > 0) && (
                      <p style={{ color: cmhcDiff > 0 ? '#dc2626' : '#16a34a' }}>
                        • CMHC insurance: {cmhcDiff > 0 ? '+' : ''}${cmhcDiff.toLocaleString()}
                        {cmhcDiff > 0 ? ' more' : ' less'}
                      </p>
                    )}
                    <p style={{ fontWeight: 600, paddingTop: '0.5rem', borderTop: '1px solid rgba(12, 15, 36, 0.1)', color: totalDiff > 0 ? '#dc2626' : '#16a34a' }}>
                      • Total cost over life of mortgage: {totalDiff > 0 ? '+' : ''}${totalDiff.toLocaleString()}
                    </p>
                  </>
                );
              })()}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ScenarioComparison;
